/**
 * Экран смерти: свидетельство (DESIGN-SYSTEM.md «Свидетельство о смерти»),
 * «последняя страница» (G16: три важных года) и последние слова (A10).
 * В таблице — мечта («сбылась в 34» / «не сбылась», core-loop-v2 §4), под
 * ней — вехи строками документа (`life.milestones`), не значками.
 *
 * Страница неподвижна (`.still`), часы не создаются; акцент на экране не
 * используется (правило смерти). Тяжёлая причина — блок помощи всегда,
 * независимо от `skipHeavy`; при `skipHeavy` причина на свидетельстве не
 * называется (D3: меняются тексты, не статистика).
 *
 * Правки (важные годы, последние слова) пишутся в стор через
 * `game.setEpitaph` — это флаги жизни, они уходят в сохранение и в карточку.
 * `readonly` — просмотр с полки: без редакторов и «Родиться снова»; жизнь
 * тогда приходит пропсом `life` (стор не трогается), а `onBack` — «Назад».
 */
import type { Dataset } from '@life/data';
import { YEAR_SUMMARY_KEY, type Life } from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { HelpBlock } from '../components/HelpBlock';
import { LIFE_MAP_CELLS } from '../components/LifeMap';
import { ThemeToggle } from '../components/ThemeToggle';
import { certificateFacts, countryLabel, dreamLine, expectedYears, keyYearsOf, lastWordsOf } from '../lib/certificate';
import { downloadBlob, renderCertificate } from '../share/card';
import type { Game } from '../state/game';

export const KEY_YEARS_MAX = 3;
export const LAST_WORDS_MAX = 140;

export interface DeathScreenProps {
  game: Game;
  ds: Dataset;
  /** Просмотр без правок (полка завершённых жизней). */
  readonly?: boolean;
  /** Жизнь для показа вместо `game.life` (полка). */
  life?: Life;
  /** «Назад» (только в режиме просмотра). */
  onBack?: () => void;
}

/** Неподвижная карта жизни: прожитые — чернила, важные годы — точкой, остальное — пунктир. */
function StillMap({ life, marked }: { life: Life; marked: ReadonlySet<number> }) {
  const cells = Array.from({ length: LIFE_MAP_CELLS }, (_, age) => {
    const on = age < life.age;
    const cls = on ? (marked.has(age) ? 'on starred' : 'on') : 'off';
    return <i key={age} class={cls} data-age={age} />;
  });
  return (
    <div class="strip" role="img" aria-label={t('ui.death.map')}>
      {cells}
    </div>
  );
}

interface KeyYearsProps {
  life: Life;
  chosen: number[];
  readonly: boolean;
  onChange: (years: number[]) => void;
}

/** G16: три слота важных лет — из ☆-лет одним тапом или любой прожитый год через поле. */
function KeyYears({ life, chosen, readonly, onChange }: KeyYearsProps) {
  const [draft, setDraft] = useState('');
  const starred = life.log.filter((e) => e.starred && e.key === YEAR_SUMMARY_KEY).map((e) => e.age);
  const candidates = [...new Set([...starred, ...chosen])].sort((a, b) => a - b);
  const full = chosen.length >= KEY_YEARS_MAX;

  const toggle = (age: number) => {
    if (chosen.includes(age)) onChange(chosen.filter((a) => a !== age));
    else if (!full) onChange([...chosen, age].sort((a, b) => a - b));
  };
  const add = () => {
    const age = Number(draft);
    // Прожитые годы — строго до возраста смерти (год смерти прожит не целиком).
    if (!Number.isInteger(age) || age < 0 || age >= life.age) return;
    toggle(age);
    setDraft('');
  };

  return (
    <div class="keyyears">
      <div class="eyebrow">{t('ui.death.keyYears')}</div>
      <div class="slots" aria-label={t('ui.death.keyYears')}>
        {Array.from({ length: KEY_YEARS_MAX }, (_, i) => (
          <span key={i} class={chosen[i] === undefined ? 'slot empty' : 'slot'}>
            {chosen[i] ?? t('ui.death.keyYears.empty')}
          </span>
        ))}
      </div>
      {!readonly && (
        <>
          <p class="hint">{t('ui.death.keyYears.hint')}</p>
          {candidates.length > 0 && (
            <div class="names" role="group" aria-label={t('ui.death.keyYears')}>
              {candidates.map((age) => {
                const picked = chosen.includes(age);
                return (
                  <button
                    type="button"
                    key={age}
                    class={picked ? 'btn primary' : 'btn'}
                    aria-pressed={picked}
                    aria-label={t('ui.death.keyYears.pick', { age })}
                    disabled={!picked && full}
                    onClick={() => toggle(age)}
                  >
                    {age}
                  </button>
                );
              })}
            </div>
          )}
          <div class="anyyear">
            <label for="key-year-any">{t('ui.death.keyYears.any')}</label>
            <input
              id="key-year-any"
              type="number"
              inputMode="numeric"
              min={0}
              max={Math.max(0, life.age - 1)}
              value={draft}
              disabled={full}
              onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
            />
            <button type="button" class="btn" disabled={full || draft === ''} onClick={add}>
              {t('ui.death.keyYears.add')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function DeathScreen({ game, ds, readonly = false, life: shown, onBack }: DeathScreenProps) {
  const life = shown ?? game.life.value;
  const [shareError, setShareError] = useState(false);
  if (life === null) return null;

  const settings = game.settings.value;
  const facts = certificateFacts(life, ds, settings.skipHeavy);
  const lastWords = lastWordsOf(life);
  const keyYears = keyYearsOf(life);
  const heavy = life.death?.heavy ?? false;
  const real = settings.realCountry;

  const share = async () => {
    try {
      const blob = await renderCertificate(life, ds, { lastWords, keyYears });
      downloadBlob(blob, t('ui.share.file.death'));
      setShareError(false);
    } catch {
      setShareError(true);
    }
  };

  const rows: Array<[string, string]> = [
    [t('ui.death.lived'), facts.lived],
    [t('ui.death.cause'), facts.cause],
    [t('ui.death.education'), facts.education],
    [t('ui.death.work'), facts.work],
    [t('ui.death.family'), facts.family],
    [t('ui.death.moves'), facts.moves],
    [t('ui.death.dream'), dreamLine(life)],
  ];

  return (
    <main class="page still death-page">
      <header class="topbar">
        <b>{t('ui.app.title')}</b>
        <ThemeToggle game={game} />
      </header>
      <section class="cert">
        <div class="eyebrow">{t('ui.death.eyebrow')}</div>
        <h1>{facts.name}</h1>
        <div class="sub">{facts.span}</div>
        <dl>
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
        </dl>
        {life.milestones.length > 0 && (
          <div class="milestones-block">
            <div class="eyebrow">{t('ui.death.milestones')}</div>
            <ul class="milestones">
              {life.milestones.map((id) => (
                <li key={id}>{t(`milestone.${id}`)}</li>
              ))}
            </ul>
          </div>
        )}
        <StillMap life={life} marked={new Set(keyYears)} />
        {lastWords.trim() !== '' && <div class="quote">«{lastWords.trim()}»</div>}
        {heavy && <HelpBlock />}
        {real !== null && (
          <ul class="compare">
            {(() => {
              const own = expectedYears(ds, life.iso3, life);
              const theirs = expectedYears(ds, real, life);
              return (
                <>
                  <li>
                    {t('ui.death.compare.own', {
                      country: countryLabel(ds, life.iso3),
                      year: life.birthYear,
                      n: own,
                      years: yearsWord(own),
                    })}
                  </li>
                  <li>{t('ui.death.compare.real', { country: countryLabel(ds, real), n: theirs, years: yearsWord(theirs) })}</li>
                </>
              );
            })()}
          </ul>
        )}
      </section>

      <section class="epitaph">
        <KeyYears
          life={life}
          chosen={keyYears}
          readonly={readonly}
          onChange={(years) => game.setEpitaph(lastWords, years)}
        />
        {!readonly && (
          <div class="field">
            <label for="last-words">{t('ui.death.lastWords')}</label>
            <textarea
              id="last-words"
              rows={2}
              maxLength={LAST_WORDS_MAX}
              value={lastWords}
              onInput={(e) => game.setEpitaph((e.currentTarget as HTMLTextAreaElement).value, keyYears)}
            />
            <p class="hint">
              {t('ui.death.lastWords.hint', { max: LAST_WORDS_MAX })}{' '}
              <span class="mono">{t('ui.death.lastWords.count', { n: lastWords.length, max: LAST_WORDS_MAX })}</span>
            </p>
          </div>
        )}
        <div class="actions">
          <button type="button" class="btn" onClick={() => void share()}>
            {t('ui.death.share')}
          </button>
          {!readonly && (
            <button type="button" class="btn ghost" onClick={() => game.bornAgain()}>
              {t('ui.death.again')}
            </button>
          )}
          {readonly && onBack && (
            <button type="button" class="btn ghost" onClick={onBack}>
              {t('ui.death.back')}
            </button>
          )}
        </div>
        {shareError && (
          <div class="alert warn" role="status">
            {t('ui.death.share.fail')}
          </div>
        )}
      </section>
    </main>
  );
}
